import muxjs from "mux.js";

export type TsRemuxV3Stats = {
    inputBytes: number;
    outputBytes: number;
    fragments: number;
    indexEntries: number;
};

export type TsRemuxerV3 = {
    /** Transmuxes one complete MPEG-TS HLS segment into an MP4 fragment. */
    transform(segment: Uint8Array): Uint8Array;
    /** Finalizes the fragmented MP4 timeline. */
    flush(): Uint8Array;
    readonly stats: TsRemuxV3Stats;
};

function concat(parts: Uint8Array[]) {
    if (parts.length === 0) return new Uint8Array(0);
    if (parts.length === 1) return parts[0];

    const output = new Uint8Array(parts.reduce((size, part) => size + part.length, 0));
    let offset = 0;

    for (const part of parts) {
        output.set(part, offset);
        offset += part.length;
    }

    return output;
}

function equalBytes(left: Uint8Array, right: Uint8Array) {
    if (left.length !== right.length) return false;

    for (let index = 0; index < left.length; index++) {
        if (left[index] !== right[index]) return false;
    }

    return true;
}

type Mp4Box = {
    type: string;
    start: number;
    contentStart: number;
    end: number;
};

type FragmentIndexEntry = {
    time: number;
    moofOffset: number;
    trafNumber: number;
};

function readUint32(data: Uint8Array, offset: number) {
    return data[offset] * 0x1000000 +
        data[offset + 1] * 0x10000 +
        data[offset + 2] * 0x100 +
        data[offset + 3];
}

function readUint64(data: Uint8Array, offset: number) {
    return readUint32(data, offset) * 0x100000000 + readUint32(data, offset + 4);
}

function readBoxes(data: Uint8Array, start = 0, end = data.length) {
    const boxes: Mp4Box[] = [];
    let offset = start;

    while (offset + 8 <= end) {
        let size = readUint32(data, offset);
        const type = String.fromCharCode(...data.subarray(offset + 4, offset + 8));
        let headerSize = 8;

        if (size === 1 && offset + 16 <= end) {
            size = readUint64(data, offset + 8);
            headerSize = 16;
        } else if (size === 0) {
            size = end - offset;
        }

        if (size < headerSize || offset + size > end) break;
        boxes.push({
            type,
            start: offset,
            contentStart: offset + headerSize,
            end: offset + size,
        });
        offset += size;
    }

    return boxes;
}

function findVideoTrackId(initSegment: Uint8Array) {
    const moov = readBoxes(initSegment).find(box => box.type === "moov");
    if (!moov) return null;

    for (const trak of readBoxes(initSegment, moov.contentStart, moov.end).filter(box => box.type === "trak")) {
        const children = readBoxes(initSegment, trak.contentStart, trak.end);
        const tkhd = children.find(box => box.type === "tkhd");
        const mdia = children.find(box => box.type === "mdia");
        if (!tkhd || !mdia) continue;

        const hdlr = readBoxes(initSegment, mdia.contentStart, mdia.end)
            .find(box => box.type === "hdlr");
        if (!hdlr) continue;

        const handlerType = String.fromCharCode(...initSegment.subarray(
            hdlr.contentStart + 8,
            hdlr.contentStart + 12,
        ));
        if (handlerType !== "vide") continue;

        const version = initSegment[tkhd.contentStart];
        return readUint32(initSegment, tkhd.contentStart + (version === 1 ? 20 : 12));
    }

    return null;
}

function findMoofTrackId(data: Uint8Array, moof: Mp4Box) {
    const traf = readBoxes(data, moof.contentStart, moof.end)
        .find(box => box.type === "traf");
    if (!traf) return null;

    const tfhd = readBoxes(data, traf.contentStart, traf.end)
        .find(box => box.type === "tfhd");
    return tfhd ? readUint32(data, tfhd.contentStart + 4) : null;
}

function makeBox(type: string, payload: Uint8Array) {
    const box = new Uint8Array(8 + payload.length);
    writeUint32(box, 0, box.length);
    writeType(box, 4, type);
    box.set(payload, 8);
    return box;
}

function combineTrackFragments(data: Uint8Array, videoTrackId: number | null) {
    if (videoTrackId === null) return data;

    const topLevelBoxes = readBoxes(data);
    const groups = topLevelBoxes
        .map((moof, index) => {
            if (moof.type !== "moof" || topLevelBoxes[index + 1]?.type !== "mdat") return null;

            const children = readBoxes(data, moof.contentStart, moof.end);
            const mfhd = children.find(box => box.type === "mfhd");
            const traf = children.find(box => box.type === "traf");
            const mdat = topLevelBoxes[index + 1];
            if (!mfhd || !traf) return null;

            return {
                mfhd: data.slice(mfhd.start, mfhd.end),
                traf: data.slice(traf.start, traf.end),
                media: data.slice(mdat.contentStart, mdat.end),
                trackId: findMoofTrackId(data, moof),
            };
        })
        .filter(group => group !== null);
    if (groups.length !== 2) return data;

    groups.sort((left, right) =>
        Number(left.trackId === videoTrackId) - Number(right.trackId === videoTrackId)
    );

    const moof = makeBox("moof", concat([
        groups[0].mfhd,
        ...groups.map(group => group.traf),
    ]));
    const trafs = readBoxes(moof, 8, moof.length).filter(box => box.type === "traf");
    let mediaOffset = moof.length + 8;

    trafs.forEach((traf, index) => {
        const children = readBoxes(moof, traf.contentStart, traf.end);
        const tfhd = children.find(box => box.type === "tfhd");
        const trun = children.find(box => box.type === "trun");
        if (tfhd) {
            // With multiple traf boxes in one moof, make every trun offset relative
            // to the moof rather than inheriting the previous track fragment's base.
            moof[tfhd.contentStart + 1] |= 0x02;
        }
        if (trun) {
            const flags =
                moof[trun.contentStart + 1] * 0x10000 +
                moof[trun.contentStart + 2] * 0x100 +
                moof[trun.contentStart + 3];
            if ((flags & 0x01) !== 0) {
                writeUint32(moof, trun.contentStart + 8, mediaOffset);
            }
        }
        mediaOffset += groups[index].media.length;
    });

    const mdat = makeBox("mdat", concat(groups.map(group => group.media)));
    return concat([moof, mdat]);
}

function writeUint32(data: Uint8Array, offset: number, value: number) {
    data[offset] = Math.floor(value / 0x1000000) & 0xff;
    data[offset + 1] = Math.floor(value / 0x10000) & 0xff;
    data[offset + 2] = Math.floor(value / 0x100) & 0xff;
    data[offset + 3] = value & 0xff;
}

function writeUint64(data: Uint8Array, offset: number, value: number) {
    writeUint32(data, offset, Math.floor(value / 0x100000000));
    writeUint32(data, offset + 4, value % 0x100000000);
}

function writeType(data: Uint8Array, offset: number, type: string) {
    for (let index = 0; index < 4; index++) {
        data[offset + index] = type.charCodeAt(index);
    }
}

function buildTfra(trackId: number, entries: FragmentIndexEntry[]) {
    const entrySize = 19;
    const data = new Uint8Array(24 + entries.length * entrySize);
    writeUint32(data, 0, data.length);
    writeType(data, 4, "tfra");
    data[8] = 1;
    writeUint32(data, 12, trackId);
    writeUint32(data, 20, entries.length);

    entries.forEach((entry, index) => {
        const offset = 24 + index * entrySize;
        writeUint64(data, offset, entry.time);
        writeUint64(data, offset + 8, entry.moofOffset);
        data[offset + 16] = entry.trafNumber;
        data[offset + 17] = 1;
        data[offset + 18] = 1;
    });

    return data;
}

function buildMfra(indexByTrack: Map<number, FragmentIndexEntry[]>) {
    if (indexByTrack.size === 0) return new Uint8Array(0);

    const tfras = Array.from(indexByTrack, ([trackId, entries]) => buildTfra(trackId, entries));
    const size = 8 + tfras.reduce((total, tfra) => total + tfra.length, 0) + 16;
    const mfra = new Uint8Array(size);
    writeUint32(mfra, 0, size);
    writeType(mfra, 4, "mfra");

    let offset = 8;
    for (const tfra of tfras) {
        mfra.set(tfra, offset);
        offset += tfra.length;
    }

    writeUint32(mfra, offset, 16);
    writeType(mfra, offset + 4, "mfro");
    writeUint32(mfra, offset + 12, size);
    return mfra;
}

export function createTsRemuxerV3(): TsRemuxerV3 {
    const transmuxer = new muxjs.mp4.Transmuxer({
        keepOriginalTimestamps: false,
        remux: true,
    });
    const stats: TsRemuxV3Stats = {
        inputBytes: 0,
        outputBytes: 0,
        fragments: 0,
        indexEntries: 0,
    };

    let initSegment: Uint8Array | null = null;
    let videoTrackId: number | null = null;
    let pendingParts: Uint8Array[] = [];
    let pendingError: unknown;
    let fileOffset = 0;
    const indexByTrack = new Map<number, FragmentIndexEntry[]>();

    transmuxer.on("data", segment => {
        if (initSegment === null) {
            initSegment = segment.initSegment.slice();
            videoTrackId = findVideoTrackId(initSegment);
            pendingParts.push(segment.initSegment);
            fileOffset += segment.initSegment.length;
        } else if (!equalBytes(initSegment, segment.initSegment)) {
            pendingError = new Error("The stream changed codecs and cannot be saved as one MP4 file");
            return;
        }

        const mediaData = combineTrackFragments(segment.data, videoTrackId);

        for (const moof of readBoxes(mediaData).filter(box => box.type === "moof")) {
            const trafs = readBoxes(mediaData, moof.contentStart, moof.end)
                .filter(box => box.type === "traf");

            trafs.forEach((traf, index) => {
                const children = readBoxes(mediaData, traf.contentStart, traf.end);
                const tfhd = children.find(box => box.type === "tfhd");
                const tfdt = children.find(box => box.type === "tfdt");
                if (!tfhd || !tfdt) return;

                const trackId = readUint32(mediaData, tfhd.contentStart + 4);
                const version = mediaData[tfdt.contentStart];
                const time = version === 1
                    ? readUint64(mediaData, tfdt.contentStart + 4)
                    : readUint32(mediaData, tfdt.contentStart + 4);
                const entries = indexByTrack.get(trackId) ?? [];

                entries.push({
                    time,
                    moofOffset: fileOffset + moof.start,
                    trafNumber: index + 1,
                });
                indexByTrack.set(trackId, entries);
                stats.indexEntries++;
            });
        }

        pendingParts.push(mediaData);
        fileOffset += mediaData.length;
        stats.fragments++;
    });
    transmuxer.on("error", error => {
        pendingError = error;
    });

    function drain() {
        if (pendingError !== undefined) {
            const error = pendingError;
            pendingError = undefined;
            throw error instanceof Error ? error : new Error(String(error));
        }

        const output = concat(pendingParts);
        pendingParts = [];
        stats.outputBytes += output.length;
        return output;
    }

    return {
        stats,
        transform(segment) {
            stats.inputBytes += segment.length;
            transmuxer.push(segment);
            transmuxer.flush();
            return drain();
        },
        flush() {
            transmuxer.endTimeline();
            const mfra = buildMfra(indexByTrack);
            if (mfra.length > 0) {
                pendingParts.push(mfra);
                fileOffset += mfra.length;
            }
            return drain();
        },
    };
}
